import type { BadgeProps } from '@wartek-id/badge'

import type { SerializedNode } from '@craftjs/core'
import type { AxiosError } from 'axios'
import type { ReactNode } from 'react'

/**
 * Types
 */
export type ActivityType = 'text' | 'video' | 'quiz'

export type AksiNyataAcceptedFormats = 'video' | 'pdf' | 'image'

export type AksiNyataActionType = 'CHOOSE' | 'CONFIRM' | 'DRAFT' | 'PUBLISH'

export enum AksiNyataSessionState {
  NotCreated = 'NOT_CREATED',
  Created = 'CREATED',
  Confirmed = 'CONFIRMED',
  Completed = 'COMPLETED', // aksi nyata sudah di-publish oleh guru, dan akan/sedang dalam proses evaluasi
  Undetermined = 'UNDETERMINED',
  NeedRevision = 'NEED_REVISION', // aksi nyata tidak lulus proses evaluasi dan perlu diperbaikin oleh guru
  Validated = 'VALIDATED', // aksi nyata lulus proses evaluasi dan guru dapat masuk ke proses untuk memproses pembuatan sertifikat
  Appeal = 'APPEAL', // aksi nyata yang need revision sedang dilakukan banding oleh user
}

export type PostTestState = 'not_started' | 'not_passed' | 'passed'

export type QuizType = 'multiple' | 'single'

// imported from guru-frontend - app/KomunitasPage/partials/Detail/CommunicationList.tsx
export type TCommunicationType = 'FB' | 'TELEGRAM' | 'WA'

/**
 * Common Request Error Response
 */
interface MILError {
  code: string
  message: string
}

export interface MilErrorResponse {
  errors: MILError[]
  meta?: any
}

export interface MilUseQueryError extends AxiosError {
  originalError: {
    response: {
      data: Partial<MilErrorResponse>
    }
  }
}

// For better type naming
export type TMilCommonErrorResponse = MilUseQueryError

/**
 * Response for useGetTopicList
 * Note: Percentage is soon deprecated and removed from response
 */
export interface Topic {
  id: string | number
  name: string
  duration: number
  totalModule: number
  description: string
  headerImage: string
  certifiable: boolean
  href?: string
  percentage?: number
  badge?: {
    state?: 'validated' | 'ongoing' | 'completed'
    type: BadgeProps['variant']
    text: string
  }
}

/**
 * Response for useGetTopicDetail
 */
export interface TopicDetails {
  modules: Module[]
  name: string
  values: string[]
  aksiNyataOptionCount?: number
  contextualizedPageId?: number
  topicType: 'DALJAB' | 'PMM'
  isTopicUpdated?: boolean
}

/**
 * Aksi Nyata Related Types
 */

/**
 * Response for useGetTopicAksiNyataOptions
 */
export interface AksiNyataOption {
  aksiNyataOptionId: number
  title: string
  guidance: string
  descriptionQuestion: string
  reflectionQuestion: string
  acceptedFormats: AksiNyataAcceptedFormats[]
}

export interface AksiNyataFeatured {
  aksiNyataId: number
  author: string
  date: string
  id: string
  thumbnailUrl: string
  title: string
  topicId: number
  topicName: string
  avatarUrl?: string
}

export interface AksiNyataCreator {
  id?: number | string
  avatarUrl: string
  email: string
  name: string
}

export interface AksiNyataAttachment {
  id: number
  type: string
  name?: string
  url?: string
}

/**
 * Response for useGetTopicAksiNyataSession
 */
export interface AksiNyataSession {
  id?: string | number
  description: string
  reflection: string
  selectedOption: AksiNyataOption
  state: AksiNyataSessionState
  title: string
  attachments: AksiNyataAttachment[] | []
  creator: AksiNyataCreator
  reviewerName: string
  reviewerUserID: string
}

export interface PublicAksiNyata extends Omit<AksiNyataSession, 'creator'> {
  feedbackFormId: number | string
  author?: AksiNyataCreator
  creator?: AksiNyataCreator
}

/**
 * Request body for usePostAksiNyata
 */
export interface AksiNyataCreateBody {
  aksiNyataOptionId?: number
  action?: AksiNyataActionType
  title?: string
  description?: string
  reflection?: string
  aksiNyataAttachmentIds?: number[]
  selectedFormat?: string
}

/**
 * Response for useGetAksiNyataFeedback
 */
export interface AksiNyataFeedback {
  author: AksiNyataCreator
  submission: any
  createdAt: string
}

/**
 * Response for useGetMateriText
 */
export interface ResponseMaterialTextData {
  id: string
  name: string
  contents: string[]
}

/**
 * Response for useGetMateriVideo
 */
export interface ResponseMaterialVideoData {
  id: string
  name: string
  url: string
  description: string
  isVertical: boolean
}

/**
 * Response for useGetQuiz
 */
export interface ResponseQuiz {
  name: string
  questions: Question[]
  completed?: boolean
  progress?: 'NOT_STARTED' | 'COMPLETED' | 'SHOULD_RETRY'
}

/**
 * Response for useGetReflection
 */
export interface MateriReflection {
  question: string
  answer: string
  completed: boolean
}

/**
 * Response for usePostRefleksi
 */
export interface PostRefleksiResponse {
  data: {
    ID: string
    reflective: { answer: string }
  }
}

/**
 * Request body for usePostQuizProgress
 */
export interface QuizAnswer {
  questionId: number | null
  answer: string | string[]
  isCorrect?: boolean
}

/**
 * Response for useGetModule Preview
 */
export interface ResponseModulData {
  id: string
  name: string
  description: string
  duration: number
  reviewer: ModuleReviewer
  imageUrl?: string
  learningMaterial: {
    completed: boolean
    count: number
    materials: Material[]
    progress: 'NOT_STARTED' | 'COMPLETED' | 'SHOULD_RETRY'
  }
  postTest: {
    name: string
    state: PostTestState
    submittedTime: string
  }
  moduleType: 'DALJAB' | 'PMM'
}

/**
 * Response for usePostQuizProgress & useGetMaterialByModule
 */
export interface MaterialDataResponse {
  id: string
  name: string
  completed: boolean
  reflective: {
    question: string
    answer: string
    completed?: boolean
  }
  contents: MateriContent[]
  quiz: ResponseQuiz
  shouldRetry: boolean
}

/**
 * Response for useGetPostTestSession & usePostPostTestSession
 */
export interface CreatePostTestSessionData {
  failedMaterial?: FailedMaterial[]
  id: number
  moduleId: number
  questions: Question[]
  sessionComplete: boolean
  state: PostTestState
}

/**
 * Request body for usePostPostTestSession
 */
export interface PostTestAnswerParams {
  questionId: number
  answer: String[]
}

export interface PostTestRequestBody {
  posttest: PostTestAnswerParams[]
}

/**
 * Progress Related Interfaces
 */
export enum PostTestStatus {
  NotStarted = 'not_started',
  Passed = 'passed',
  NotPassed = 'not_passed',
}

export interface ModuleProgress {
  moduleId: number
  material: boolean
  postTest:
    | PostTestStatus.NotStarted
    | PostTestStatus.NotPassed
    | PostTestStatus.Passed
    | boolean
  prerequisiteCompleted: boolean
  imagePath: string
  name: string
}

/**
 * Response for useGetTopicsProgress
 */
export interface OverallProgress {
  id: string | number
  percentage?: number
  name: string
  imageUrl: string
  modules: ModuleProgress[]
  aksiNyata: AksiNyataSession
}

/**
 * Props Related Interfaces
 */
export interface ActivityItemProps {
  name: string
  href: string
  type: ActivityType
  isComplete?: boolean
  isShouldRetry?: boolean
  trackingParams?: {
    topik_id?: number | string
    module_id?: number | string
    materi_id?: number | string
  }
  isActive?: boolean
}

export interface ConfirmNavigateAwayDialogProps {
  title?: string
  description?: string
  children?: ReactNode
  cancelText?: string
  confirmText?: string
  isOpen: boolean
  icon?: ReactNode
  onClose: () => void
  onConfirmed: () => void
}

export interface DetailPopupProps {
  containerSpecs: {
    left: number
    width: number
  }
  closeButtonHandler: () => void
  children: ReactNode
  iconClass?: string
  className?: string
}

export interface FailedMaterial {
  id: number
  title: string
}

export interface Material {
  id: string
  name: string
  isComplete?: boolean
  completed?: boolean
}

export interface MateriContent {
  id: number
  name: string
  type: ActivityType
  completed?: boolean // deprecated
  progress?: 'NOT_STARTED' | 'COMPLETED' | 'SHOULD_RETRY'
}

export interface Module {
  id: string | number
  name: string
  duration: number
  totalMaterial: number
  number?: number
  image?: string //Note: deprecated as on latest design
  progress?: ModuleProgress | undefined
  badgeText?: string | undefined
}

export interface ModuleReviewer {
  imageUrl?: string
  name: string
  about: string
}

interface QuizOption {
  key: string
  text: string
  isCorrect?: boolean
}

export interface Question {
  id: number
  name: string
  text: string
  type: QuizType
  answerOptions: QuizOption[]
  answer?: {
    key: string
    text: string
  }
  IsCorrect?: boolean
}

export interface QuestionIndicatorProps {
  currentIndex: number
  totalQuestions: number
}

export interface QuizOptionsProps {
  options: QuizOption[]
  onSelected: (hasAnswer: boolean, answer: string | string[]) => void
  page?: number
  showAnswers?: boolean
}

export interface TopicDetailProps {
  title: string
  pointers: string[]
  modules: Topic[]
}

export interface AksiNyataFeedback {
  author: AksiNyataCreator
  submission: any
  createdAt: string
}

export interface LearningSeriesGoals {
  title: string
  topicIds: Array<number>
  draftTopics?: TDraftTopic[]
}

type TDraftTopic = {
  id: number
  title: string
  thumbnailImageUrl: string
}

/**
 * Response for learning series key value
 */
export interface LearningSeries {
  id: string
  title: string
  description: string
  figureImageUrl: string
  coverImageUrl: string
  goals: Array<LearningSeriesGoals>
}

export type TOwnedCommunity = {
  id: string
  imgSrc: string
  memberCount: number
  name: string
}

export type TFollowedCommunity = Omit<TOwnedCommunity, 'memberCount'>

export type TPenggerakInfo = {
  avatarUrl: string
  email: string
  id: string
  name: string
  occupation: string
  eligible?: boolean
}

export type TExternalItem = {
  link: string
  type: TCommunicationType
}

export type TCommunity = {
  id: string
  version: number
  followed: boolean
  name: string
  followerCount: number
  information: string
  province: string
  district: string
  jenjang: string[]
  provinceId: string
  jenjangIds: string[]
  penggerakItems: TPenggerakInfo[]
  externalItems: TExternalItem[]
  thumbnailUrl: string
}

export type TAPIGetOwnedCommunity = {
  community: TCommunity
}

export type TAPIPostSearchCommunity = {
  community: TCommunity
}[]

export type TAPIGetCommunity = {
  community: TCommunity
}

export type TAPIPostCommunity = Pick<
  TCommunity,
  | 'name'
  | 'version'
  | 'information'
  | 'thumbnailUrl'
  | 'externalItems'
  | 'jenjangIds'
  | 'provinceId'
>

export type TAPIPostCommunityInformation = Pick<
  TCommunity,
  'name' | 'version' | 'information' | 'thumbnailUrl'
>

export type TAPIPostCommunityCommunication = Pick<
  TCommunity,
  'externalItems' | 'version'
>

export interface ICommunityWebinar {
  id: string
  title: string
  description: string
  createdAt: string
  startDate: string
  endDate: string
  links: {
    zoom: string
  }
  speakers: {
    name: string
    occupation: string
  }[]
  community: {
    id: string
    name: string
    followerCount: number
    thumbnailUrl: string
  }
  version?: string
  registered?: boolean
}

export type TAPIPostCommunityWebinar = Pick<
  ICommunityWebinar,
  'id' | 'title' | 'startDate' | 'endDate' | 'registered'
> & {
  community: Pick<ICommunityWebinar['community'], 'id' | 'name'>
}

export type TAPIGetCommunityWebinar = ICommunityWebinar

export type TAPIGetWebinarEnrollment = {
  enrolled: boolean
  enrollmentCount: number
}

export type TAPIPostCommunityWebinarEnrollResponse = {
  enrolled: boolean
}

export type TAPIGetCommunityWebinarCreationPermissions = {
  eligible: boolean
  prerequisiteTopic: {
    id: number
    name: string
  }
}

export type TAPIPostCreateCommunityWebinarRequest = {
  title: string
  description: string
  startDate: string
  endDate: string
  links: {
    zoom: string
  }
  speakers: {
    name: string
    occupation: string
  }[]
  communityId: string
}

export type TAPIPostCreateCommunityWebinarResponse = {
  id: string
}

export type TAPIPutEditCommunityWebinarRequest = {
  id: string
  title: string
  description: string
  startDate: string
  endDate: string
  links: {
    zoom: string
  }
  speakers: {
    name: string
    occupation: string
  }[]
  version?: string
}

export type TAPIPutEditCommunityWebinarResponse = {
  null
}

export enum AppealState {
  NotCreated = 'NOT_CREATED',
  Submitted = 'SUBMITTED',
  Accepted = 'ACCEPTED',
  Declined = 'DECLINED',
}

export type TAPIGetAksiNyataRevisionResponse = {
  topicId: string
  revisionItems: string[]
  appeal: {
    state: AppealState
    quota: number
    review: string
  }
}

export type TAPIPostCertificateData = {
  userProfile: {
    name: string
    nip: string
    nuptk: string
    birthplace: string
    birthdate: string
    occupation: string
    unit: string
  }
}

export type TAPIPostTopicCertificateData = {
  userProfile: {
    name: string
    nip: string
    unit: string
  }
}

export type TAPIGetProfileResponse = {
  email: string
  name: string
  nip: string
  nuptk: string
  birthplace: string
  birthdate: string
  occupation: string
  unit: string
}

type ProfileCertificate = {
  email: string
  name: string
  nip: string
  nuptk: string
  birthplace: string
  birthdate: string
  occupation: string
  unit: string
}

type ProfileCertificateTopic = {
  name: string
  nip: string
  unit: string
}

export interface TAPIGetCertificateTopicDetailResponse
  extends Omit<TAPIGetCertificateResponse, 'profile'> {
  profile: ProfileCertificateTopic
}

export interface TAPIGetCertificateResponse {
  profile: ProfileCertificate
  certificateImage: {
    back: string
    front?: string
    pdf?: string
  }
  certificateWorkUnitName: string
  certificateNumber: string
  certificationDate: string
  signatureName: string
  signatureOccupation: string
  topicName: string
  certificateState: 'COMPLETED' | 'NOT_CREATED' | 'PROCESSING' | 'FAILED' | ''
}

export type TAPIGetAdminValidity = {
  id: string
  avatarUrl: string
  email: string
  name: string
}

export type TAPIPostAddNewAdminResponse = {
  message: string
}

export type TAPIPostAddNewAdminRequest = {
  id: string
  email: string
  occupation: string
}

export type TAPIDeleteRemoveAdminResponse = {
  message: string
}

export type TAPIDeleteRemoveAdminRequest = {
  id: string
  email: string
}

export type TSpeakerData = {
  name: string
  occupation: string
}

export interface IWebinarFormData {
  title: string
  description: string
  startDate: string
  endDate: string
  zoomLink: string
  speakers: TSpeakerData[]
  version?: string
}

export interface APIPostLMSConnect {
  client_id: string
  response_type: string
  redirect_uri: string
  scope: string
  state: string
  guru_token: string
}

export interface ResponseLMSConnect {
  code: string
}

export interface APIPostAksiNyataAppeal {
  appeal: string
}

export interface ResponseAksiNyataAppeal {
  aksiNyataValidationId: number
}

export type TAPIGetCreateCommunityEligibility = {
  null
}

export type TCommunityExternalItems = {
  link: string
  type: string
}

export type TCommunityPenggerak = {
  name?: string
  email: string
  occupation: string
}

export type TAPIPostRegisterCommunityRequest = {
  name: string
  information: string
  jenjang: string[]
  province: string
  district: string
  subject: string[]
  communityType: string
  targetProfession: string[]
  externalItems: TCommunityExternalItems[]
  penggerakItems: TCommunityPenggerak[]
}

export type TAPIPostRegisterCommunityResponse = {
  null
}

export interface TabbedNavItem {
  name: string
  label: string
  href?: string
  image?: string
}

// Contextualized Learning / Kontekstualisasi Belajar / CL / KB
export enum CLWidgetType {
  ExternalAssessment = 'Assessment',
  ExternalToolkit = 'Toolkit',
  InternalTopic = 'Topic',
  InternalModule = 'Module',
  InternalLearningActivity = 'AksiNyata',
  InternalPageCard = 'PageCard',
  StaticVideoCard = 'VideoCard',
  StaticHeading = 'Heading',
  StaticText = 'Text',
  StaticButton = 'Button',
  StaticDocument = 'Document',
  StaticContainer = 'Container',
  RootContentWrapper = 'ContentWrapper',
}

export interface CLWidgetObject {
  widgetNode: string
  widgetType: CLWidgetType
  responseData: Record<string, unknown>
}

export interface CLWidgetSchema {
  [anyKey: string]: SerializedNode
}

export interface CLItem {
  id: string
  title: string
  excerpt: string
  thumbnailUrl: string
  contentSchema: string
  widgetObjects: CLWidgetObject[]
}

export interface TTopicNewest {
  title: string
  topicData: TTopicNewestDetail[]
}

export interface TTopicNewestDetail {
  topicID: number
  topicName: string
  totalModule: number
  label: string
  imagePath: string
}

export type TTopicRecommendationResponseData = {
  fase: string
  id: string
  kelas: string
  thumbnailUrl: string
  subtitle?: string
  title: string
  toolkitType: string
}

export type TTopicRecommendationContextualize = {
  id: number
  pageExcerpt: string
  pageThumbnailUrl: string
  pageTitle: string
  label: string
}

export type TAPIGetTopicRecommendation = {
  title: string
  subtitle: string
  contextualizePages: TTopicRecommendationContextualize[]
}

/* topic list type */
export type TTopicList = {
  id: number
  name: string
  duration: number
  certifiable: boolean
  totalModule: number
  description: string
  headerImage: string
  contextualizePageId: number
  percentage: number
  aksiNyataState: AksiNyataSessionState
}

export type TAPIGetTopicNewest = TTopicNewest

export type ResponseRerenderCertificatePDF = {
  message: string
}
